import React, { useState } from "react";
import { Columns3, X, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";

/**
 * Lightweight per-admin column customiser.
 *
 * Props:
 *   - columns: [{ id: string, label: string, locked?: boolean }]
 *             `locked` columns are always visible and can't be reordered away
 *             from their position (e.g. "Name" as the first column).
 *   - value: { order: string[], hidden: string[] }  — current prefs
 *   - onChange: ({order, hidden}) => void           — fires after every edit
 *   - testid: string
 *
 * We deliberately keep this dependency-free (no DnD library). Admins reorder
 * with ▲/▼ arrows and toggle visibility with the eye icon — slower than drag
 * but works reliably on mobile + desktop.
 */
export default function ColumnOrderMenu({ columns, value, onChange, testid = "column-order-menu" }) {
  const [open, setOpen] = useState(false);
  const order = value?.order?.length
    ? value.order.filter((id) => columns.some((c) => c.id === id))
    : columns.map((c) => c.id);
  // Append any new columns that aren't in the saved order yet.
  for (const c of columns) if (!order.includes(c.id)) order.push(c.id);
  const hidden = new Set(value?.hidden || []);

  const move = (id, delta) => {
    const idx = order.indexOf(id);
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= order.length) return;
    const next = [...order];
    const col = columns.find((c) => c.id === id);
    // Respect locked columns — you can't push them out of first position.
    const swapCol = columns.find((c) => c.id === next[newIdx]);
    if (col?.locked || swapCol?.locked) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange({ order: next, hidden: Array.from(hidden) });
  };

  const toggleHidden = (id) => {
    const col = columns.find((c) => c.id === id);
    if (col?.locked) return;
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange({ order, hidden: Array.from(next) });
  };

  const reset = () => onChange({ order: columns.map((c) => c.id), hidden: [] });

  return (
    <div className="relative inline-block" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm text-[#5D2C8F] inline-flex items-center gap-1.5 hover:bg-[#FAF6EE]"
        data-testid={`${testid}-trigger`}
      >
        <Columns3 size={14} /> Columns
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-72 bg-white border border-[#E5E0D8] rounded-xl shadow-xl z-40 p-3"
            data-testid={`${testid}-panel`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-[#5D2C8F] font-medium uppercase tracking-wide">Reorder &amp; hide</div>
              <button onClick={() => setOpen(false)} className="text-[#A685BD]" aria-label="Close"><X size={14} /></button>
            </div>
            <div className="text-[11px] text-[#6E6074] mb-2 leading-snug">Your layout — syncs across any device you log in from.</div>
            <ul className="space-y-1">
              {order.map((id, i) => {
                const col = columns.find((c) => c.id === id);
                if (!col) return null;
                const isHidden = hidden.has(id);
                return (
                  <li key={id} className="flex items-center gap-1 bg-[#FAF6EE] rounded px-2 py-1.5" data-testid={`${testid}-row-${id}`}>
                    <button
                      onClick={() => move(id, -1)}
                      disabled={i === 0 || col.locked}
                      className="text-[#5D2C8F] disabled:opacity-30 p-0.5"
                      aria-label={`Move ${col.label} up`}
                      data-testid={`${testid}-up-${id}`}
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      onClick={() => move(id, 1)}
                      disabled={i === order.length - 1 || col.locked}
                      className="text-[#5D2C8F] disabled:opacity-30 p-0.5"
                      aria-label={`Move ${col.label} down`}
                      data-testid={`${testid}-down-${id}`}
                    >
                      <ArrowDown size={12} />
                    </button>
                    <span className={`flex-1 text-xs ${isHidden ? "text-[#9A8DA6] line-through" : "text-[#3A2A47]"}`}>
                      {col.label}
                      {col.locked && <span className="ml-1 text-[10px] text-[#A685BD]">(locked)</span>}
                    </span>
                    <button
                      onClick={() => toggleHidden(id)}
                      disabled={col.locked}
                      className="text-[#5D2C8F] disabled:opacity-30 p-0.5"
                      aria-label={isHidden ? `Show ${col.label}` : `Hide ${col.label}`}
                      data-testid={`${testid}-toggle-${id}`}
                    >
                      {isHidden ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                  </li>
                );
              })}
            </ul>
            <button onClick={reset} className="mt-3 text-[11px] text-[#A685BD] hover:underline" data-testid={`${testid}-reset`}>Reset to default</button>
          </div>
        </>
      )}
    </div>
  );
}
